import { observer } from 'mobx-react';
import { Button, Form, FormGroup, Input } from 'reactstrap';
import { Contract, ContractInfo, Modal, ModalHandle, ServicesAlert, Tooltip, Enums } from 'Mri.Web.Common';
import { useStore } from '../hooks/use-store';
import React, { useEffect, useMemo, useState } from 'react';
import DatePicker from 'react-datepicker';
import { isValid } from 'date-fns';
import Select from 'react-select';
import { LinkContainer } from 'react-router-bootstrap';
import { ServiceLevelPresetClass } from './ServiceLevelPresets';

interface props {
    readonly modalHandle: ModalHandle;
    readonly contract: Contract;
    readonly display?: { shippedDate?: Date };
    readonly viewState: 'create' | 'edit' | 'readOnly';
    readonly accountName?: string;
}

export const ContractModal = observer((props: props) => {
    const { modalHandle, contract, display, viewState, accountName } = props;
    const { accountStore, contractsStore } = useStore();
    const { serviceLevelPresets } = new ServiceLevelPresetClass();
    const [contractProperties, setContractProperty] = useState<Contract>(new Contract(contract));
    const [viewMode, setViewMode] = useState<'create' | 'edit' | 'readOnly'>(viewState);
    const [isLoading, setIsLoading] = useState<boolean>();
    const [initialValidate, setInitialValidate] = useState(false);
    const [errors, setErrors] = useState({
        serviceCO: (value: string) => {
            if (value === undefined || value.length === 0) {
                return 'Service CO is Required';
            }
            return '';
        },
        startDate: (value: Date) => {
            if (value === undefined) {
                return 'Start Date is Required';
            }
            return '';
        },
        endDate: (value: Date) => {
            if (value === undefined) {
                return 'End Date is Required';
            }
            return '';
        },
    } as any);

    const confirmDeleteModalHandle = useMemo(() => new ModalHandle(), []);

    useEffect(() => {
        if (viewMode === 'create') setInitialValues();
    }, [modalHandle.show]);

    const setInitialValues = () => {
        const initialServiceLevel = Mri.Contracts.ServiceLevel.MRIStandardWarranty;
        const initialPresets = serviceLevelPresets.get(initialServiceLevel) ?? serviceLevelPresets.get(0);
        setContractProperty({
            ...contractProperties,
            ...({
                serviceCO: '',
                contractNumber: '',
                startDate: new Date(),
                endDate: new Date(Date.now() + 1000 * 60 * 60 * 24 * 7),
                firstShipmentDate: undefined,
                lastShipmentDate: undefined,
                coveredProducts: undefined,
                fieldServicesProvider: undefined,
                contractNotes: undefined,
                active: true,
                serviceLevel: initialServiceLevel,
                term: 1,
            } as any),
            ...initialPresets,
        });
    };

    const reset = () => {
        if (viewState === 'readOnly') setViewMode('readOnly');
        setInitialValidate(false);
        setIsLoading(false);
    };

    const saveContract = async (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        event.preventDefault();
        setIsLoading(true);
        if (!contractProperties) return;

        if (!fieldsAreValid()) {
            // If any of the fields are not valid then we can not proceed
            setInitialValidate(true);
            setIsLoading(false);
            return;
        }

        if (viewMode === 'edit') {
            await contractsStore
                .updateContractAsync(contractProperties)
                .catch(e => console.error(e))
                .then(() => setIsLoading(false))
                .finally(() => modalHandle.close());
        } else if (viewMode === 'create') {
            await contractsStore
                .createAsync(contractProperties)
                .catch(e => console.error(e))
                .then(() => setIsLoading(false))
                .finally(() => modalHandle.close());
        }
    };

    const fieldsAreValid = () => {
        let result = true;
        Object.keys(errors).forEach(key => {
            const contractKey = key as keyof Contract;
            if (errors[key](contractProperties[contractKey]).length !== 0) {
                result = false;
            }
        });
        return result;
    };

    const deleteContractAsync = async () => {
        await contractsStore.deleteAsync(contract.serviceCO);
        confirmDeleteModalHandle.close();
        modalHandle.close();
    };

    const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (!(event.target.type === 'checkbox')) event.preventDefault();

        if (!contractProperties) {
            return;
        }

        const target = event.target;
        const name = target.id as keyof Contract;
        const value = target.type === 'checkbox' ? target.checked : target.value;

        const updatedValue = { [name]: value } as any;

        try {
            setContractProperty({ ...contractProperties, ...updatedValue });
        } catch (e: any) {
            console.error(e);
        }
    };

    const onDateChange = (date: Date | [Date, Date] | null, id: string) => {
        if (date && isValid(date)) {
            const updatedValue = { [id]: date } as any;

            try {
                setContractProperty({ ...contractProperties, ...updatedValue });
            } catch (e: any) {
                console.error(e);
            }
        }
    };

    const onSelectChange = (value: any) => {
        const updatedValue = { serviceLevel: value } as any;
        const presets = serviceLevelPresets.get(value) ?? serviceLevelPresets.get(0);
        try {
            setContractProperty({
                ...contractProperties,
                ...updatedValue,
                ...presets,
            });
        } catch (e: any) {
            console.error(e);
        }
    };

    const getSelectEnumOptions = () => {
        const options = [];
        for (const [key, value] of Enums.ServiceLevel) {
            options.push({ label: value, value: key });
        }
        return options;
    };

    const renderTitle = (
        <div>
            <div>
                <h2 className="d-inline-block">
                    {viewMode == 'readOnly'
                        ? 'Contract CO: ' + contract?.serviceCO
                        : viewMode == 'edit'
                            ? 'Edit Contract CO: ' + contract?.serviceCO
                            : viewMode == 'create'
                                ? 'Create Contract'
                                : 'N/A'}
                </h2>
            </div>
        </div>
    );

    const ConfirmDeleteContractModalFooter = (
        <>
            <div className="confirm-footer">
                <Button color={'danger'} onClick={deleteContractAsync}>
                    Delete
                </Button>
                <Button
                    color={'secondary'}
                    onClick={() => {
                        confirmDeleteModalHandle.close();
                    }}
                >
                    Cancel
                </Button>
            </div>
        </>
    );

    const DeleteContractModal = ({ deleteContract, handle }: { deleteContract: Contract; handle: ModalHandle }) => (
        <Modal
            className="modal-sm"
            title="Confirm Deletion"
            modalHandle={handle}
            btnClass="w-50"
            footerClass="d-flex justify-content-center"
            footer={ConfirmDeleteContractModalFooter}
        >
            <div className="text-center">{`Delete ${deleteContract.serviceCO}?`}</div>
        </Modal>
    );

    const inputs = (
        <Form onSubmit={() => saveContract} className="row">
            <div className="col-xl-6">
                <FormGroup switch>
                    <label>Active</label>
                    <Input
                        id="active"
                        type="switch"
                        checked={contractProperties.active ?? true}
                        onChange={onChange}
                        disabled={viewMode == 'readOnly'}
                    />
                </FormGroup>
                <FormGroup>
                    {initialValidate &&
                        errors.serviceCO &&
                        errors.serviceCO(contractProperties.serviceCO).length !== 0 && (
                        <p className="alert-danger">{errors.serviceCO(contractProperties.serviceCO)}</p>
                    )}
                    <label>Service CO</label>

                    <Input
                        type="text"
                        id="serviceCO"
                        value={contractProperties.serviceCO ?? ''}
                        disabled={viewMode == 'readOnly'}
                        onChange={onChange}
                        required
                    />
                </FormGroup>
                <FormGroup>
                    {initialValidate &&
                        errors.contractNumber &&
                        errors.contractNumber(contractProperties.contractNumber).length !== 0 && (
                        <p className="alert-danger">{errors.contractNumber(contractProperties.contractNumber)}</p>
                    )}
                    <label>Contract Number</label>

                    <Input
                        type="text"
                        id="contractNumber"
                        value={contractProperties.contractNumber ?? ''}
                        onChange={onChange}
                        disabled={viewMode == 'readOnly'}
                        required
                    />
                </FormGroup>
                <FormGroup className="d-inline-block">
                    {initialValidate &&
                        errors.startDate &&
                        errors.startDate(contractProperties.startDate).length !== 0 && (
                        <p className="alert-danger">{errors.startDate(contractProperties.startDate)}</p>
                    )}
                    <div>
                        <label>Start Date</label>
                    </div>
                    <div>
                        <DatePicker
                            id="startDate"
                            className="form-control"
                            selected={contractProperties.startDate}
                            onChange={date => onDateChange(date, 'startDate')}
                            disabled={viewMode == 'readOnly'}
                        />
                    </div>
                </FormGroup>
                <FormGroup className="ms-3 d-inline-block">
                    {initialValidate && errors.endDate && errors.endDate(contractProperties.endDate).length !== 0 && (
                        <p className="alert-danger">{errors.endDate(contractProperties.endDate)}</p>
                    )}
                    <div>
                        <label>End Date</label>
                    </div>
                    <div>
                        <DatePicker
                            id="endDate"
                            className="form-control"
                            selected={contractProperties.endDate}
                            onChange={date => onDateChange(date, 'endDate')}
                            disabled={viewMode == 'readOnly'}
                        />
                    </div>
                </FormGroup>
                <div>
                    <FormGroup className="d-inline-block">
                        {initialValidate &&
                            errors.firstShipmentDate &&
                            errors.firstShipmentDate(contractProperties.firstShipmentDate).length !== 0 && (
                            <p className="alert-danger">
                                {errors.firstShipmentDate(contractProperties.firstShipmentDate)}
                            </p>
                        )}
                        <div>
                            <label>First Shipment Date</label>
                        </div>

                        <div>
                            <DatePicker
                                id="firstShipmentDate"
                                className="form-control"
                                selected={contractProperties.firstShipmentDate}
                                onChange={date => onDateChange(date, 'firstShipmentDate')}
                                disabled={viewMode == 'readOnly'}
                            />
                        </div>
                    </FormGroup>
                    <FormGroup className="ms-3 d-inline-block">
                        {initialValidate &&
                            errors.lastShipmentDate &&
                            errors.lastShipmentDate(contractProperties.lastShipmentDate).length !== 0 && (
                            <p className="alert-danger">
                                {errors.lastShipmentDate(contractProperties.lastShipmentDate)}
                            </p>
                        )}
                        <div>
                            <label>Last Shipment Date</label>
                        </div>

                        <div>
                            <DatePicker
                                id="lastShipmentDate"
                                className="form-control"
                                selected={contractProperties.lastShipmentDate}
                                onChange={date => onDateChange(date, 'lastShipmentDate')}
                                disabled={viewMode == 'readOnly'}
                            />
                        </div>
                    </FormGroup>
                </div>
                <FormGroup>
                    {initialValidate && errors.term && errors.term(contractProperties.term).length !== 0 && (
                        <p className="alert-danger">{errors.term(contractProperties.term)}</p>
                    )}
                    <label>Term (In Months)</label>

                    <Input
                        type="number"
                        id="term"
                        required
                        value={contractProperties.term ?? 0}
                        onChange={onChange}
                        disabled={viewMode == 'readOnly'}
                    />
                </FormGroup>
                <FormGroup>
                    {initialValidate &&
                        errors.coveredProduct &&
                        errors.coveredProduct(contractProperties.coveredProduct).length !== 0 && (
                        <p className="alert-danger">{errors.coveredProduct(contractProperties.coveredProduct)}</p>
                    )}
                    <label>Covered Product</label>

                    <Input
                        type="text"
                        id="coveredProduct"
                        required
                        value={contractProperties.coveredProduct ?? ''}
                        onChange={onChange}
                        disabled={viewMode == 'readOnly'}
                    />
                </FormGroup>
                <FormGroup>
                    {initialValidate &&
                        errors.warranty &&
                        errors.warranty(contractProperties.warranty).length !== 0 && (
                        <p className="alert-danger">{errors.warranty(contractProperties.warranty)}</p>
                    )}
                    <label>Warranty</label>

                    <Input
                        type="text"
                        id="warranty"
                        value={contractProperties.warranty ?? ''}
                        onChange={onChange}
                        required
                        disabled={viewMode == 'readOnly'}
                    />
                </FormGroup>
                <FormGroup>
                    {initialValidate &&
                        errors.technicalSupport &&
                        errors.technicalSupport(contractProperties.technicalSupport).length !== 0 && (
                        <p className="alert-danger">
                            {errors.technicalSupport(contractProperties.technicalSupport)}
                        </p>
                    )}
                    <label>Technical Support</label>

                    <Input
                        type="text"
                        id="technicalSupport"
                        value={contractProperties.technicalSupport ?? ''}
                        onChange={onChange}
                        required
                        disabled={viewMode == 'readOnly'}
                    />
                </FormGroup>
            </div>

            <div className="col-xl-6">
                <FormGroup>
                    {initialValidate &&
                        errors.fruReplacements &&
                        errors.fruReplacements(contractProperties.fruReplacements).length !== 0 && (
                        <p className="alert-danger">{errors.fruReplacements(contractProperties.fruReplacements)}</p>
                    )}
                    <label>Field Replaceable Units</label>

                    <Input
                        type="text"
                        id="fruReplacements"
                        value={contractProperties.fruReplacements ?? ''}
                        onChange={onChange}
                        required
                        disabled={viewMode == 'readOnly'}
                    />
                </FormGroup>
                <FormGroup>
                    {initialValidate &&
                        errors.nonFruReplacements &&
                        errors.nonFruReplacements(contractProperties.nonFruReplacements).length !== 0 && (
                        <p className="alert-danger">
                            {errors.nonFruReplacements(contractProperties.nonFruReplacements)}
                        </p>
                    )}
                    <label>Non Field Replaceable Units</label>

                    <Input
                        type="text"
                        id="nonFruReplacements"
                        value={contractProperties.nonFruReplacements ?? ''}
                        onChange={onChange}
                        required
                        disabled={viewMode == 'readOnly'}
                    />
                </FormGroup>
                <FormGroup>
                    {initialValidate &&
                        errors.fieldServicesProvider &&
                        errors.fieldServicesProvider(contractProperties.fieldServicesProvider).length !== 0 && (
                        <p className="alert-danger">
                            {errors.fieldServicesProvider(contractProperties.fieldServicesProvider)}
                        </p>
                    )}
                    <label>Field Services Provider</label>

                    <Input
                        type="text"
                        id="fieldServicesProvider"
                        value={contractProperties.fieldServicesProvider ?? ''}
                        onChange={onChange}
                        required
                        disabled={viewMode == 'readOnly'}
                    />
                </FormGroup>
                <FormGroup>
                    {initialValidate &&
                        errors.contractNotes &&
                        errors.contractNotes(contractProperties.contractNotes).length !== 0 && (
                        <p className="alert-danger">{errors.contractNotes(contractProperties.contractNotes)}</p>
                    )}
                    <label>Contract Notes</label>

                    <Input
                        type="text"
                        id="contractNotes"
                        value={contractProperties.contractNotes ?? ''}
                        onChange={onChange}
                        required
                        disabled={viewMode == 'readOnly'}
                    />
                </FormGroup>

                <FormGroup>
                    {initialValidate &&
                        errors.serviceLevel &&
                        errors.serviceLevel(contractProperties.serviceLevel).length !== 0 && (
                        <p className="alert-danger">{errors.serviceLevel(contractProperties.serviceLevel)}</p>
                    )}
                    <label>Service Level</label>

                    <Select
                        id={'serviceLevel'}
                        options={viewMode == 'readOnly' ? undefined : getSelectEnumOptions()}
                        onChange={v => onSelectChange(v?.value)}
                        className="service-level-select"
                        value={getSelectEnumOptions().filter(
                            option => option.value === contractProperties.serviceLevel
                        )}
                        disabled={viewMode == 'readOnly'}
                    />
                </FormGroup>
                <FormGroup switch>
                    {initialValidate &&
                        errors.firmwareUpdates &&
                        errors.firmwareUpdates(contractProperties.firmwareUpdates).length !== 0 && (
                        <p className="alert-danger">{errors.firmwareUpdates(contractProperties.firmwareUpdates)}</p>
                    )}
                    <label>Firmware Updates</label>

                    <Input
                        id="firmwareUpdates"
                        type="switch"
                        checked={contractProperties.firmwareUpdates ?? false}
                        onChange={onChange}
                        disabled={viewMode == 'readOnly'}
                    />
                </FormGroup>
                <FormGroup switch>
                    {initialValidate &&
                        errors.customerPortal &&
                        errors.customerPortal(contractProperties.customerPortal).length !== 0 && (
                        <p className="alert-danger">{errors.customerPortal(contractProperties.customerPortal)}</p>
                    )}
                    <label>Customer Portal</label>

                    <Input
                        id="customerPortal"
                        type="switch"
                        checked={contractProperties.customerPortal ?? false}
                        onChange={onChange}
                        disabled={viewMode == 'readOnly'}
                    />
                </FormGroup>
                <FormGroup switch>
                    {initialValidate &&
                        errors.monitoringManagedServices &&
                        errors.monitoringManagedServices(contractProperties.monitoringManagedServices).length !== 0 && (
                        <p className="alert-danger">
                            {errors.monitoringManagedServices(contractProperties.monitoringManagedServices)}
                        </p>
                    )}
                    <label>Monitoring Managed Services</label>

                    <Input
                        id="monitoringManagedServices"
                        type="switch"
                        checked={contractProperties.monitoringManagedServices ?? false}
                        onChange={onChange}
                        disabled={viewMode == 'readOnly'}
                    />
                </FormGroup>
                <FormGroup switch>
                    {initialValidate &&
                        errors.advancedMonitoring &&
                        errors.advancedMonitoring(contractProperties.advancedMonitoring).length !== 0 && (
                        <p className="alert-danger">
                            {errors.advancedMonitoring(contractProperties.advancedMonitoring)}
                        </p>
                    )}
                    <label>Advanced Monitoring</label>

                    <Input
                        id="advancedMonitoring"
                        type="switch"
                        checked={contractProperties.advancedMonitoring ?? false}
                        onChange={onChange}
                        disabled={viewMode == 'readOnly'}
                    />
                </FormGroup>
                <FormGroup switch>
                    {initialValidate && errors.api && errors.api(contractProperties.api).length !== 0 && (
                        <p className="alert-danger">{errors.api(contractProperties.api)}</p>
                    )}
                    <label>API</label>

                    <Input id="api" type="switch" checked={contractProperties.api ?? false} onChange={onChange} />
                </FormGroup>
                <FormGroup switch>
                    {initialValidate &&
                        errors.managedServices &&
                        errors.managedServices(contractProperties.managedServices).length !== 0 && (
                        <p className="alert-danger">{errors.managedServices(contractProperties.managedServices)}</p>
                    )}
                    <label>Managed Services</label>

                    <Input
                        id="managedServices"
                        type="switch"
                        checked={contractProperties.managedServices ?? false}
                        onChange={onChange}
                        disabled={viewMode == 'readOnly'}
                    />
                </FormGroup>
                <FormGroup switch>
                    {initialValidate &&
                        errors.onsiteFieldServices &&
                        errors.onsiteFieldServices(contractProperties.onsiteFieldServices).length !== 0 && (
                        <p className="alert-danger">
                            {errors.onsiteFieldServices(contractProperties.onsiteFieldServices)}
                        </p>
                    )}
                    <label>On-site Field Services</label>

                    <Input
                        id="onsiteFieldServices"
                        type="switch"
                        checked={contractProperties.onsiteFieldServices ?? false}
                        onChange={onChange}
                        disabled={viewMode == 'readOnly'}
                    />
                </FormGroup>
                <FormGroup switch>
                    {initialValidate &&
                        errors.m2mDevice &&
                        errors.m2mDevice(contractProperties.m2mDevice).length !== 0 && (
                        <p className="alert-danger">{errors.m2mDevice(contractProperties.m2mDevice)}</p>
                    )}
                    <label>M2M Device</label>

                    <Input
                        id="m2mDevice"
                        type="switch"
                        checked={contractProperties.m2mDevice ?? false}
                        onChange={onChange}
                        disabled={viewMode == 'readOnly'}
                    />
                </FormGroup>
                <FormGroup switch>
                    {initialValidate &&
                        errors.proofOfPlay &&
                        errors.proofOfPlay(contractProperties.proofOfPlay).length !== 0 && (
                        <p className="alert-danger">{errors.proofOfPlay(contractProperties.proofOfPlay)}</p>
                    )}
                    <label>Proof of Play</label>

                    <Input
                        id="proofOfPlay"
                        type="switch"
                        checked={contractProperties.proofOfPlay ?? false}
                        onChange={onChange}
                        disabled={viewMode == 'readOnly'}
                    />
                </FormGroup>
                <FormGroup switch>
                    {initialValidate &&
                        errors.customerOwnedParts &&
                        errors.customerOwnedParts(contractProperties.customerOwnedParts).length !== 0 && (
                        <p className="alert-danger">
                            {errors.customerOwnedParts(contractProperties.customerOwnedParts)}
                        </p>
                    )}
                    <label>Customer Owned Parts</label>

                    <Input
                        id="customerOwnedParts"
                        type="switch"
                        checked={contractProperties.customerOwnedParts ?? false}
                        onChange={onChange}
                        disabled={viewMode == 'readOnly'}
                    />
                </FormGroup>
                <FormGroup switch>
                    {initialValidate &&
                        errors.eventManagement &&
                        errors.eventManagement(contractProperties.eventManagement).length !== 0 && (
                        <p className="alert-danger">{errors.eventManagement(contractProperties.eventManagement)}</p>
                    )}
                    <label>Event Management</label>

                    <Input
                        id="eventManagement"
                        type="switch"
                        checked={contractProperties.eventManagement ?? false}
                        onChange={onChange}
                        disabled={viewMode == 'readOnly'}
                    />
                </FormGroup>
                <FormGroup switch>
                    {initialValidate && errors.demo && errors.demo(contractProperties.demo).length !== 0 && (
                        <p className="alert-danger">{errors.demo(contractProperties.demo)}</p>
                    )}
                    <label>Demo</label>

                    <Input id="demo" type="switch" checked={contractProperties.demo ?? false} onChange={onChange} />
                </FormGroup>
                <FormGroup switch>
                    {initialValidate &&
                        errors.advancedWarrantyAlerts &&
                        errors.advancedWarrantyAlerts(contractProperties.advancedWarrantyAlerts).length !== 0 && (
                        <p className="alert-danger">
                            {errors.advancedWarrantyAlerts(contractProperties.advancedWarrantyAlerts)}
                        </p>
                    )}
                    <label>Advanced Warranty Alerts</label>

                    <Input
                        id="advancedWarrantyAlerts"
                        type="switch"
                        checked={contractProperties.advancedWarrantyAlerts ?? false}
                        onChange={onChange}
                        disabled={viewMode == 'readOnly'}
                    />
                </FormGroup>
            </div>
        </Form>
    );

    const footer = (
        <>
            {accountStore.isInRoles(['MriAdmin']) && viewMode == 'readOnly' && (
                <>
                    <Tooltip message="Edit Displays" delay={0}>
                        <LinkContainer exact to={`accounts/${contract.serviceCO}`}>
                            <Button color="primary">Edit Displays</Button>
                        </LinkContainer>
                    </Tooltip>

                    <Tooltip message="Edit" delay={0}>
                        <Button color="primary" onClick={() => setViewMode('edit')}>
                            Edit
                        </Button>
                    </Tooltip>

                    <Tooltip message="Delete" delay={0}>
                        <Button color="danger" onClick={confirmDeleteModalHandle.open}>
                            Delete
                        </Button>
                    </Tooltip>
                </>
            )}
            {viewMode != 'readOnly' && (
                <Tooltip message="Save" delay={0}>
                    <Button color="success" onClick={e => saveContract(e)}>
                        Submit
                    </Button>
                </Tooltip>
            )}
            <Tooltip message="Cancel" delay={0}>
                <Button color="primary" onClick={modalHandle.close}>
                    Cancel
                </Button>
            </Tooltip>
        </>
    );

    const MainModal = (
        <Modal title={renderTitle} modalHandle={modalHandle} size="xl" footer={footer} onClosed={() => reset()}>
            <div>
                <p className="d-inline-block px-2">Account: {accountName}</p>

                {viewMode != 'create' && (
                    <div className="d-inline-block px-2">
                        <ServicesAlert type="message" contract={contract} display={display} />
                    </div>
                )}
            </div>

            <div className="card-body p-3 phone-modal">
                {isLoading && <h1>...Loading</h1>}
                {!isLoading && !contract && viewMode == 'readOnly' && (
                    <div className="text-center">Contract Not Found</div>
                )}

                {!isLoading && viewMode === 'readOnly' && <ContractInfo contract={contract} display={display} />}
                {!isLoading && viewMode !== 'readOnly' && inputs}
            </div>
            <DeleteContractModal deleteContract={contract} handle={confirmDeleteModalHandle} />
        </Modal>
    );

    return MainModal;
});
